import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  CreateGHADiscountRuleBody,
  ListGHADiscountRulesFilters,
  UpdateGHADiscountRuleBody,
  createGHADiscountRule,
  listGHADiscountRules,
  setGHADiscountRuleEnabled,
  updateGHADiscountRule,
} from '~/services/ConnectionGoogleService';

export const ghaDiscountRuleQueryKeys = {
  all: ['ghaDiscountRules'] as const,
  lists: () => [...ghaDiscountRuleQueryKeys.all, 'list'] as const,
  list: (filters: ListGHADiscountRulesFilters) => [...ghaDiscountRuleQueryKeys.lists(), filters] as const,
};

export function useGetGHADiscountRules(filters: ListGHADiscountRulesFilters = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ghaDiscountRuleQueryKeys.list(filters),
    queryFn: async ({ signal }) => {
      const response = await listGHADiscountRules(filters, { signal });
      return response.result;
    },
    enabled: options.enabled ?? true,
  });
}

export function useCreateGHADiscountRule() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (input: CreateGHADiscountRuleBody) => {
      return await createGHADiscountRule(input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ghaDiscountRuleQueryKeys.lists() });
      enqueueSnackbar('GHA discount rule created', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to create GHA discount rule: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useUpdateGHADiscountRule() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateGHADiscountRuleBody }) => {
      return await updateGHADiscountRule(id, input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ghaDiscountRuleQueryKeys.lists() });
      enqueueSnackbar('GHA discount rule updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to update GHA discount rule: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useSetGHADiscountRuleEnabled() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      return await setGHADiscountRuleEnabled(id, enabled);
    },
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: ghaDiscountRuleQueryKeys.lists() });
      enqueueSnackbar(`GHA discount rule ${variables.enabled ? 'enabled' : 'disabled'}`, { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to update GHA discount rule status: ${error.message}`, { variant: 'error' });
    },
  });
}
